import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import cn from 'classnames';

import { getOrganizationDomain } from 'Shared/selectors/organization';
import TextField from 'Shared/components/TextField/TextField';
import PrepopulatedTextField from 'Shared/components/PrepopulatedTextField/PrepopulatedTextField';
import ConnectedCalendarIcon from 'Shared/components/ConnectedCalendarIcon/ConnectedCalendarIcon';
import { useTranslation } from 'Shared/utils/hooks/use-translation';

import WorkspaceTeammatesSuggestionsByEmail from '../WorkspaceTeammatesSuggestionsByEmail/WorkspaceTeammatesSuggestionsByEmail';
import './CreateTeammateByEmailField.scss';
import { WpIcon } from 'Wpds/components/icon';

type Props = {
  index: number;
  identif: string;
  formValues: {};
  valuesCount: number;
  isSuggestedListOpened: boolean;
  formValue: {
    fullName: string;
    teammateEmail: string;
    recurringMeetingId: number | null;
    errorMessage: string | null;
    userId?: number | null;
    fromCalendar?: boolean;
  };
  onEmailInputChange: Function;
  onNameInputChange: Function;
  onRemoveItem: Function;
  onRemoveRecurringMeetingId?: Function;
  onSuggestionClick: Function;
  onSetIsSuggestedListOpened: Function;
};

function CreateTeammateByEmailField({
  index,
  identif,
  valuesCount,
  formValues,
  isSuggestedListOpened,
  formValue,
  onRemoveItem,
  onSuggestionClick,
  onEmailInputChange,
  onNameInputChange,
  onSetIsSuggestedListOpened,
}: Props) {
  const { tr } = useTranslation();
  const [isEmailFieldFocused, setIsEmailFieldFocused] = useState(false);

  const organizationDomain = useSelector(getOrganizationDomain);

  const handleEmailInputChange = event => onEmailInputChange(event, identif);

  const handleNameInputChange = event => onNameInputChange(event, identif);

  const handleRemoveItem = () => onRemoveItem(identif);

  const handleTextInputFocus = () => {
    setIsEmailFieldFocused(true);
    onSetIsSuggestedListOpened(true);
  };

  const handleTextInputBlur = () =>
    setTimeout(() => {
      setIsEmailFieldFocused(false);
      onSetIsSuggestedListOpened(true);
    }, 500);

  return (
    <div className="create-teammate-by-email-field">
      <div key={identif}>
        <div className="d-flex align-items-end">
          <PrepopulatedTextField
            type="text"
            label={index === 0 ? tr('perform.workspace.simple_bulk_invite.email_label') + '*' : null}
            labelMuted={false}
            placeholder={tr('perform.workspace.simple_bulk_invite.email_placeholder')}
            prepopulatedText={String(organizationDomain || '')}
            wrapperClassName="mb-2 mr-2"
            className={cn('bulk-invite-temmates-modal-input py-1', {
              'create-teammate-by-email-field--recurring-meeting': formValue.recurringMeetingId,
            })}
            value={formValue.teammateEmail}
            onChange={handleEmailInputChange}
            onBlur={handleTextInputBlur}
            onFocus={handleTextInputFocus}
            inputIcon={formValue.recurringMeetingId && <ConnectedCalendarIcon />}
          />
          <TextField
            type="text"
            label={index === 0 ? tr('perform.workspace.simple_bulk_invite.name_optional_label') : null}
            labelMuted={false}
            placeholder={tr('perform.workspace.simple_bulk_invite.name_placeholder')}
            wrapperClassName="mb-2"
            className="bulk-invite-temmates-modal-input py-1"
            value={formValue.fullName}
            onChange={handleNameInputChange}
          />

          {valuesCount > 1 ? (
            <span
              className={`bulk-invite-temmates-modal-delete-icon align-self-center ml-2 ${
                index === 0 ? 'mt-3' : 'mb-1'
              }`}
              onClick={handleRemoveItem}
            >
              <WpIcon prefix="fas" name="times-circle" />
            </span>
          ) : (
            <div className="mr-4" />
          )}
        </div>
        <WorkspaceTeammatesSuggestionsByEmail
          identif={identif}
          formValues={formValues}
          isSuggestedListOpened={isSuggestedListOpened && isEmailFieldFocused}
          teammateEmailValue={formValue.teammateEmail}
          onSuggestionClick={onSuggestionClick}
        />
        {formValue.errorMessage && (
          <div className="alert alert-danger px-2 py-1 mb-2 small" role="alert">
            {formValue.errorMessage}
          </div>
        )}
      </div>
    </div>
  );
}

export default CreateTeammateByEmailField;
